import React from 'react';

import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import pick from 'util/pick';
import {ElementMouseEvent} from 'util/Types';

interface CommonArchiveButtonProps {
  buttonStyle?: ButtonStyle;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
  fullWidth?: boolean;
  showText?: boolean;
  tooltipTitle?: string;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

interface ArchiveButtonOnClickProps extends CommonArchiveButtonProps {
  onClick: (e: ElementMouseEvent) => void;
}

interface ArchiveButtonOnArchiveProps extends CommonArchiveButtonProps {
  initialPurpose?: string;
  items: Array<ArchiveModal.ArchiveModalItem>;
  onArchive: (itemIds: Array<string>, purpose: string, options?: ArchiveModal.ArchiveOptionsForModal) => void;
  shouldDetectShortcuts?: boolean;
}

type ArchiveButtonProps = ArchiveButtonOnArchiveProps | ArchiveButtonOnClickProps;

export default function ArchiveButton({
  buttonStyle = ButtonStyle.TERTIARY_BLUE,
  className,
  disabled,
  fullWidth,
  showText,
  tooltipTitle = 'Archive',
  ...props
}: ArchiveButtonProps): JSX.Element {
  const handleClick = (e: ElementMouseEvent): void => {
    if (props.onClickTracking) {
      props.onClickTracking();
    }
    // eslint-disable-next-line benchlint/no-in-operator
    if ('onClick' in props) {
      props.onClick(e);
    } else {
      e.stopPropagation();
      ArchiveModal.open({
        ...pick(props, 'initialPurpose', 'items', 'onArchive', 'shouldDetectShortcuts'),
        analyticsMetadata: props.getAnalyticsMetadata?.(),
      });
    }
  };

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        buttonStyle={buttonStyle}
        className={className}
        disabled={disabled}
        fullWidth={fullWidth}
        icon="archive"
        onClick={handleClick}
        size={ButtonSize.SMALL}
        text={showText ? 'Archive' : null}
        dataTestAttrs={{component: 'ArchiveButton', element: 'root'}}
      />
    </Tooltip>
  );
}
